/**
 * SoloAssessmentsPage - Browse available assessments and screeners
 * Unregistered users can view the list but need to register to access
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { Icons } from '../../lib/Icons';

// Static assessment templates for display
const ASSESSMENT_TEMPLATES = [
    {
        id: 'sdq',
        name: 'Strengths and Difficulties Questionnaire (SDQ)',
        description: 'A brief behavioural screening questionnaire for 2-17 year olds. Covers emotional symptoms, conduct problems, hyperactivity, peer relationships, and prosocial behaviour.',
        ageRange: '2-17 years',
        duration: '5-10 minutes',
        category: 'behaviour',
        icon: 'ClipboardDocumentCheck',
        gradient: 'from-blue-500 to-cyan-500',
        isPopular: true,
    },
    {
        id: 'snap-iv',
        name: 'SNAP-IV Rating Scale',
        description: 'A teacher and parent rating scale for ADHD symptoms. Evaluates inattention, hyperactivity, impulsivity, and oppositional defiant disorder symptoms.',
        ageRange: '5-18 years',
        duration: '10-15 minutes',
        category: 'adhd',
        icon: 'Bolt',
        gradient: 'from-amber-500 to-orange-500',
        isPopular: true,
    },
    {
        id: 'dyslexia-screener',
        name: 'Dyslexia Screening Checklist',
        description: 'An initial screening tool to identify pupils who may benefit from further assessment for dyslexia. Covers reading, writing, spelling, and phonological awareness.',
        ageRange: '5-16 years',
        duration: '10-15 minutes',
        category: 'literacy',
        icon: 'BookOpen',
        gradient: 'from-purple-500 to-violet-500',
        isPopular: true,
    },
    {
        id: 'sensory-profile',
        name: 'Sensory Profile Checklist',
        description: 'Identifies sensory processing patterns that may affect learning and behaviour. Covers auditory, visual, tactile, movement, and oral sensory processing.',
        ageRange: '3-14 years',
        duration: '15-20 minutes',
        category: 'sensory',
        icon: 'Eye',
        gradient: 'from-pink-500 to-rose-500',
    },
    {
        id: 'anxiety-screener',
        name: 'Childhood Anxiety Screener',
        description: 'Screens for generalised anxiety, separation anxiety, social anxiety, and school-related anxiety in children and young people.',
        ageRange: '6-18 years',
        duration: '5-10 minutes',
        category: 'emotional',
        icon: 'Heart',
        gradient: 'from-green-500 to-emerald-500',
    },
    {
        id: 'social-communication',
        name: 'Social Communication Questionnaire',
        description: 'A screening tool for autism spectrum characteristics. Evaluates social interaction, communication, and repetitive behaviours.',
        ageRange: '4-18 years',
        duration: '10-15 minutes',
        category: 'autism',
        icon: 'ChatBubble',
        gradient: 'from-indigo-500 to-blue-500',
    },
    {
        id: 'executive-function',
        name: 'Executive Function Checklist',
        description: 'Assesses executive function skills including working memory, flexible thinking, organisation, planning, and self-regulation.',
        ageRange: '5-18 years',
        duration: '10-15 minutes',
        category: 'cognition',
        icon: 'Brain',
        gradient: 'from-teal-500 to-cyan-500',
    },
    {
        id: 'motor-skills',
        name: 'Motor Skills Screening',
        description: 'Identifies difficulties with gross and fine motor skills, coordination, handwriting, and physical activities.',
        ageRange: '4-12 years',
        duration: '10-15 minutes',
        category: 'motor',
        icon: 'Hand',
        gradient: 'from-orange-500 to-red-500',
    },
];

// Category configuration
const CATEGORIES = [
    { id: 'all', label: 'All Assessments', Icon: Icons.Squares2x2 },
    { id: 'behaviour', label: 'Behaviour', Icon: Icons.ClipboardDocumentCheck },
    { id: 'adhd', label: 'ADHD', Icon: Icons.Bolt || Icons.ExclamationTriangle },
    { id: 'literacy', label: 'Literacy', Icon: Icons.BookOpen },
    { id: 'autism', label: 'Autism', Icon: Icons.ChatBubble },
    { id: 'emotional', label: 'Emotional', Icon: Icons.Heart },
    { id: 'sensory', label: 'Sensory', Icon: Icons.Eye },
    { id: 'cognition', label: 'Cognition', Icon: Icons.Brain },
];

// Icon mapping
const ICON_MAP = {
    ClipboardDocumentCheck: Icons.ClipboardDocumentCheck,
    Bolt: Icons.Bolt || Icons.ExclamationTriangle,
    BookOpen: Icons.BookOpen,
    Eye: Icons.Eye,
    Heart: Icons.Heart,
    ChatBubble: Icons.ChatBubble,
    Brain: Icons.Brain,
    Hand: Icons.Hand,
};

const SoloAssessmentsPage = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);

            if (currentUser) {
                try {
                    const functions = getFunctions(undefined, 'europe-west3');
                    const checkSub = httpsCallable(functions, 'checkSoloSubscriptionFunc');
                    const result = await checkSub();
                    setSubscriptionStatus(result.data);
                } catch (e) {
                    console.error('Subscription check failed:', e);
                    setSubscriptionStatus({ hasActiveSubscription: false });
                }
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const filteredAssessments = selectedCategory === 'all'
        ? ASSESSMENT_TEMPLATES
        : ASSESSMENT_TEMPLATES.filter(a => a.category === selectedCategory);

    const handleAccessAssessment = (assessmentId) => {
        if (!user) {
            navigate('/signup/solo');
            return;
        }
        if (!subscriptionStatus?.hasActiveSubscription) {
            navigate('/solo/checkout');
            return;
        }
        // For future: navigate to actual assessment
        // navigate(`/solo/assessments/${assessmentId}/run`);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500 mx-auto mb-4"></div>
                    <p className="text-slate-400">Loading assessments...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/solo/dashboard')}
                                className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
                            >
                                <Icons.ArrowLeft className="w-5 h-5 text-white" />
                            </button>
                            <img
                                src="/brand/White logo on purple.png"
                                alt="SEN-Ed"
                                className="w-10 h-10 rounded-xl"
                            />
                            <span className="text-xl font-bold text-white">Assessments & Screeners</span>
                        </div>

                        {!user ? (
                            <button
                                onClick={() => navigate('/signup/solo')}
                                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all"
                            >
                                Sign Up Free
                            </button>
                        ) : !subscriptionStatus?.hasActiveSubscription && (
                            <button
                                onClick={() => navigate('/solo/checkout')}
                                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all"
                            >
                                Unlock All Assessments
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="relative overflow-hidden py-12 md:py-16">
                <div className="absolute inset-0 bg-gradient-to-br from-rose-900/50 via-slate-900 to-pink-900/30"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
                        <Icons.ClipboardDocumentCheck className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Assessments & Screeners
                    </h1>
                    <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                        Professional screening tools to identify learning needs and inform targeted support.
                        Complete assessments digitally and generate reports instantly.
                    </p>
                </div>
            </section>

            {/* Category Filter */}
            <section className="py-6 border-b border-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap justify-center gap-3">
                        {CATEGORIES.map(category => {
                            const CategoryIcon = category.Icon;
                            const isSelected = selectedCategory === category.id;
                            const count = category.id === 'all'
                                ? ASSESSMENT_TEMPLATES.length
                                : ASSESSMENT_TEMPLATES.filter(a => a.category === category.id).length;

                            return (
                                <button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${isSelected
                                            ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg'
                                            : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                                        }`}
                                >
                                    <CategoryIcon className="w-4 h-4" />
                                    <span className="text-sm font-medium">{category.label}</span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-white/20' : 'bg-slate-700'
                                        }`}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Assessments Grid */}
            <section className="py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredAssessments.map(assessment => {
                            const AssessmentIcon = ICON_MAP[assessment.icon] || Icons.ClipboardDocumentCheck;
                            const canAccess = user && subscriptionStatus?.hasActiveSubscription;

                            return (
                                <div
                                    key={assessment.id}
                                    className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-rose-500/50 transition-all group"
                                >
                                    {/* Assessment Header with Gradient */}
                                    <div className={`h-28 bg-gradient-to-br ${assessment.gradient} p-5 flex flex-col justify-between relative`}>
                                        {/* Popular Badge */}
                                        {assessment.isPopular && (
                                            <span className="absolute top-3 right-3 px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white font-medium">
                                                Popular
                                            </span>
                                        )}

                                        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                                            <AssessmentIcon className="w-6 h-6 text-white" />
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white">
                                                {assessment.ageRange}
                                            </span>
                                            <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white">
                                                {assessment.duration}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Assessment Content */}
                                    <div className="p-5">
                                        <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-rose-400 transition-colors">
                                            {assessment.name}
                                        </h3>
                                        <p className="text-slate-400 text-sm mb-4 line-clamp-3">
                                            {assessment.description}
                                        </p>

                                        {/* Action Button */}
                                        {canAccess ? (
                                            <button
                                                onClick={() => handleAccessAssessment(assessment.id)}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-rose-500 hover:bg-rose-600 text-white font-medium rounded-xl transition-all"
                                            >
                                                <Icons.ClipboardDocumentCheck className="w-4 h-4" />
                                                Start Assessment
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleAccessAssessment(assessment.id)}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-medium rounded-xl transition-all"
                                            >
                                                <Icons.LockClosed className="w-4 h-4" />
                                                {user ? 'Subscribe to Access' : 'Sign Up to Access'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA Section for non-subscribers */}
            {!subscriptionStatus?.hasActiveSubscription && (
                <section className="py-12 bg-gradient-to-r from-rose-900/50 to-pink-900/50">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                            Unlock All Assessments & Screeners
                        </h2>
                        <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                            Get unlimited access to all screening tools, plus interventions, resource packs, the resource builder, and more.
                        </p>
                        <button
                            onClick={() => user ? navigate('/solo/checkout') : navigate('/signup/solo')}
                            className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all text-lg"
                        >
                            {user ? 'Start 7-Day Free Trial' : 'Sign Up for Free Trial'}
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
};

export default SoloAssessmentsPage;
